import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, UserPlus } from "lucide-react";

import { AppShell } from "../../components/AppShell";
import { EmptyState, LoadingRows } from "../../components/states";
import { Button } from "../../components/ui/button";
import { RequireAuth } from "../../features/auth/RequireAuth";
import { useMembership } from "../../features/event-experience/use-membership";
import type { PublicEvent } from "../../features/events/schemas";
import { usePublicEvent } from "../../features/events/use-public-event";
import { FeedbackFormPage } from "../../features/feedback/FeedbackFormPage";

// `$slug_` keeps this route out of /e/$slug's component, which renders the
// event page and has no outlet. Behind the auth gate, which renders sign-in in
// place, so a link from an announcement survives the sign-in.
export const Route = createFileRoute("/e/$slug_/feedback/$formId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { slug, formId } = Route.useParams();
  return (
    <RequireAuth>
      {(user) => (
        <FeedbackRoute
          slug={slug}
          formId={formId}
          uid={user.uid}
          email={user.email}
          signOut={user.signOut}
        />
      )}
    </RequireAuth>
  );
}

function FeedbackRoute({
  slug,
  formId,
  uid,
  email,
  signOut,
}: {
  slug: string;
  formId: string;
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const publicEvent = usePublicEvent(slug);

  if (publicEvent.status === "loading") {
    return (
      <AppShell identity={{ email }} onSignOut={signOut} title="Feedback">
        <LoadingRows rows={4} />
      </AppShell>
    );
  }
  if (publicEvent.status !== "success") {
    return (
      <AppShell identity={{ email }} onSignOut={signOut} title="Feedback">
        <EmptyState
          icon={AlertCircle}
          title="Event not found"
          testId="event-not-found"
        >
          {publicEvent.status === "error"
            ? publicEvent.error
            : "This event may have been unpublished or the link may be incorrect."}
        </EmptyState>
      </AppShell>
    );
  }

  return (
    <MemberOnly
      event={publicEvent.event}
      slug={slug}
      formId={formId}
      uid={uid}
      email={email}
      signOut={signOut}
    />
  );
}

function MemberOnly({
  event,
  slug,
  formId,
  uid,
  email,
  signOut,
}: {
  event: PublicEvent;
  slug: string;
  formId: string;
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const membership = useMembership(event.eventId, uid);

  return (
    <AppShell identity={{ email }} onSignOut={signOut} title={event.title}>
      {membership.status === "loading" && <LoadingRows rows={4} />}
      {membership.status === "error" && (
        <EmptyState icon={AlertCircle} title="Something went wrong">
          {membership.error}
        </EmptyState>
      )}
      {membership.status === "not-member" && (
        <EmptyState
          icon={UserPlus}
          title="Register to give feedback"
          tone="bold"
          testId="feedback-not-member"
          action={
            <Button variant="cta" asChild className="h-11">
              <Link to="/e/$slug" params={{ slug }}>
                Go to the event
              </Link>
            </Button>
          }
        >
          Feedback forms are for people registered for this event.
        </EmptyState>
      )}
      {membership.status === "success" && (
        <FeedbackFormPage
          eventId={event.eventId}
          slug={slug}
          formId={formId}
          uid={uid}
          timeZone={event.timezone}
        />
      )}
    </AppShell>
  );
}
